import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import ReportCharts from "@/components/ReportCharts";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Stack, Sparkle, CheckCircle, Globe, FilePdf, FileDoc, CircleNotch, Plus, XCircle, ChartLine,
  Eye, FloppyDisk,
} from "@phosphor-icons/react";

const STEPS = ["Select Template", "Choose Reference Documents", "Generate Report"];
const TYPE_ICON = { pdf: FilePdf, word: FileDoc, url: Globe };
const MONTHS = [
  { v: "01", n: "January" }, { v: "02", n: "February" }, { v: "03", n: "March" },
  { v: "04", n: "April" }, { v: "05", n: "May" }, { v: "06", n: "June" },
  { v: "07", n: "July" }, { v: "08", n: "August" }, { v: "09", n: "September" },
  { v: "10", n: "October" }, { v: "11", n: "November" }, { v: "12", n: "December" },
];
const YEARS = (() => { const y = new Date().getFullYear(); return [y + 1, y, y - 1, y - 2, y - 3].map(String); })();

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
// dd.mm.yyyy (report display format) <-> yyyy-mm-dd (native date input)
function todayDisplay() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}
function displayToInput(s) {
  if (!s) return "";
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
}
function inputToDisplay(s) {
  if (!s) return "";
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
}
function monthLabel(m) {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
}
function recentMonths(n = 12) {
  const out = []; const d = new Date();
  for (let i = 0; i < n; i++) { out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); d.setMonth(d.getMonth() - 1); }
  return out;
}

export default function GenerateReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get("draft");
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selTemplate, setSelTemplate] = useState(null);
  const [selDocs, setSelDocs] = useState([]);
  const [extraUrl, setExtraUrl] = useState("");
  const [extraUrls, setExtraUrls] = useState([]);
  const [issueNo, setIssueNo] = useState("44");
  const [issueDate, setIssueDate] = useState(todayDisplay());
  const [month, setMonth] = useState(currentMonth());
  const [carryForward, setCarryForward] = useState(false);
  const [sectionOverrides, setSectionOverrides] = useState({});
  const [generating, setGenerating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [loadedDraftId, setLoadedDraftId] = useState(null);
  const monthOptions = recentMonths(12);

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data));
  }, []);
  // load documents for the selected report month (assignable to sections)
  useEffect(() => {
    api.get(`/documents?month=${month}`).then((r) => setDocuments(r.data));
  }, [month]);

  // reopen a saved draft: load its config into the wizard
  useEffect(() => {
    if (!draftId || templates.length === 0 || loadedDraftId === draftId) return;
    api.get(`/reports/${draftId}`).then(({ data }) => {
      const tmpl = templates.find((t) => t.id === data.template_id);
      if (tmpl) setSelTemplate(tmpl);
      setIssueNo(data.issue_no || "44");
      setIssueDate(data.issue_date || todayDisplay());
      if (data.month) setMonth(data.month);
      setCarryForward(!!data.carry_forward);
      setExtraUrls(data.extra_urls || []);
      setSelDocs(data.reference_doc_ids || []);
      setSectionOverrides(data.section_overrides || {});
      setLoadedDraftId(draftId);
      toast.success("Draft loaded — continue editing");
    }).catch(() => toast.error("Could not load draft"));
  }, [draftId, templates, loadedDraftId]);

  // user changes report month -> clear stale per-section doc assignments (keep manual text)
  const changeMonth = (m) => {
    setMonth(m);
    setSectionOverrides((p) => {
      const next = {};
      Object.entries(p).forEach(([k, v]) => { next[k] = { ...v, docs: [] }; });
      return next;
    });
    setSelDocs([]);
  };

  const toggleDoc = (id) => setSelDocs((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const addUrl = () => { if (!extraUrl.trim()) return; setExtraUrls((p) => [...p, extraUrl.trim()]); setExtraUrl(""); };

  const setSectionMode = (sid, mode) => setSectionOverrides((p) => ({ ...p, [sid]: { ...(p[sid] || { manual_text: "" }), mode } }));
  const setSectionText = (sid, text) => setSectionOverrides((p) => ({ ...p, [sid]: { ...(p[sid] || {}), manual_text: text } }));
  const _updDocs = (sid, fn) => setSectionOverrides((p) => { const cur = p[sid] || {}; return { ...p, [sid]: { ...cur, docs: fn(cur.docs || []) } }; });
  const addSectionDoc = (sid) => _updDocs(sid, (docs) => [...docs, { doc_id: "", extraction_prompt: "" }]);
  const setSectionDocAt = (sid, idx, docId) => _updDocs(sid, (docs) => docs.map((d, i) => (i === idx ? { ...d, doc_id: docId } : d)));
  const setSectionDocPrompt = (sid, idx, prompt) => _updDocs(sid, (docs) => docs.map((d, i) => (i === idx ? { ...d, extraction_prompt: prompt } : d)));
  const removeSectionDoc = (sid, idx) => _updDocs(sid, (docs) => docs.filter((_, i) => i !== idx));

  const cleanedOverrides = () => {
    const cleaned = {};
    Object.entries(sectionOverrides).forEach(([k, v]) => {
      const hasText = v.manual_text && v.manual_text.replace(/<[^>]*>/g, "").trim();
      const hasDocs = (v.docs || []).some((d) => d.doc_id);
      if ((v.mode && v.mode !== "auto") || hasDocs || hasText) cleaned[k] = v;
    });
    return cleaned;
  };

  const buildPayload = () => ({
    template_id: selTemplate.id, report_id: loadedDraftId || undefined,
    issue_no: issueNo, issue_date: issueDate, month, carry_forward: carryForward,
    reference_doc_ids: selDocs, extra_urls: extraUrls, section_overrides: cleanedOverrides(),
  });

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/reports/draft", buildPayload());
      setLoadedDraftId(data.id);
      toast.success("Draft saved");
      navigate("/history?tab=drafts");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save draft");
    } finally { setSavingDraft(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/reports/generate", buildPayload());
      toast.success("Report generation started");
      navigate(`/reports/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed");
    } finally { setGenerating(false); }
  };

  const purpleCount = selTemplate?.sections?.filter((s) => (s.source_urls?.length || s.use_web_search)).length || 0;
  const docName = (id) => documents.find((d) => d.id === id)?.name || "document";

  const [previews, setPreviews] = useState({});
  const [previewing, setPreviewing] = useState("");
  const liveSources = (selTemplate?.sections || [])
    .filter((s) => s.source_urls?.length)
    .flatMap((s) => s.source_urls.map((u) => ({ section: s.title, url: u })));

  const previewUrl = async (u) => {
    setPreviewing(u);
    try {
      const { data } = await api.post("/documents/scrape", { url: u });
      setPreviews((p) => ({ ...p, [u]: data }));
    } catch { toast.error("Preview failed"); }
    finally { setPreviewing(""); }
  };

  return (
    <div className="space-y-8" data-testid="generate-page">
      <div>
        <div className="text-xs font-semibold text-[#b45309] bg-[#fef3e2] rounded-full px-3 py-1 inline-block">Report Generation</div>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mt-3">Generate New Report</h1>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 sm:gap-4 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0
                ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-[#F59E0B] text-[#1a1400]" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <CheckCircle size={18} weight="fill" /> : i + 1}
              </div>
              <span className={`text-xs sm:text-sm truncate ${i === step ? "text-slate-900 font-medium" : "text-slate-400"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="step-template">
          {templates.map((t) => {
            const sel = selTemplate?.id === t.id;
            return (
            <div key={t.id} data-testid={`template-card-${t.id}`} onClick={() => setSelTemplate(t)}
              className={`relative p-6 cursor-pointer rounded-2xl border bg-white overflow-hidden transition-all duration-200 ease-out
                ${sel ? "border-[#F59E0B] shadow-[0_10px_30px_-8px_rgba(245,158,11,0.45)] ring-2 ring-[#F59E0B] -translate-y-1 scale-[1.015]"
                       : "border-slate-200 hover:-translate-y-0.5 hover:shadow-lg hover:border-slate-300"}`}>
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#F59E0B] to-[#f6ad3d] transition-opacity duration-200 ${sel ? "opacity-100" : "opacity-0"}`} />
              {sel && (
                <div data-testid={`template-selected-${t.id}`} className="absolute top-4 right-4 select-pop">
                  <CheckCircle size={26} weight="fill" className="text-[#F59E0B]" />
                </div>
              )}
              <div className="flex items-start justify-between">
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center transition-colors ${sel ? "bg-[#fef3e2]" : "bg-[#e6f0fb]"}`}>
                  <Stack size={24} weight="duotone" className={sel ? "text-[#F59E0B]" : "text-[#004182]"} />
                </div>
                {t.is_default && !sel && <span className="text-[11px] tag-purple font-medium">Default</span>}
              </div>
              <div className="text-slate-900 font-semibold text-lg mt-4">{t.name}</div>
              <div className="text-slate-500 text-sm mt-1 line-clamp-2">{t.description}</div>
              <div className="text-xs text-slate-400 mt-4">{t.sections?.length || 0} sections</div>
            </div>
            );
          })}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-6" data-testid="step-documents">
          <div className="card p-5 flex flex-wrap items-end gap-4" data-testid="step2-month-panel">
            <div>
              <div className="label-caps text-slate-500 mb-1.5">Report Month &amp; Year</div>
              <div className="flex gap-2">
                <select data-testid="report-month-select-step2" value={month.split("-")[1]}
                  onChange={(e) => changeMonth(`${month.split("-")[0]}-${e.target.value}`)}
                  className="h-10 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm px-3 outline-none">
                  {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}
                </select>
                <select data-testid="report-year-select-step2" value={month.split("-")[0]}
                  onChange={(e) => changeMonth(`${e.target.value}-${month.split("-")[1]}`)}
                  className="w-28 h-10 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm px-3 outline-none">
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <p className="text-xs text-slate-400 flex-1 min-w-[220px]">Reference documents &amp; PIB graphs below are filtered by this month. You can also confirm the month on the final step.</p>
          </div>

          <div className="card p-4 bg-purple-50/50 border-purple-100">
            <div className="text-sm text-slate-600">
              <span className="tag-purple font-medium">Live sources</span> in this template
              ({purpleCount} sections) are scraped automatically. Optionally add extra reference documents & URLs below.
            </div>
          </div>

          {liveSources.length > 0 && (
            <div className="card p-5" data-testid="preview-panel">
              <div className="flex items-center gap-2 text-slate-900 font-semibold"><Globe size={18} className="text-emerald-600" /> Preview parsed data from live sources</div>
              <p className="text-xs text-slate-500 mt-1">Verify the scraped content before generating & publishing the report.</p>
              <div className="mt-3 space-y-2">
                {liveSources.map(({ section, url }, i) => {
                  const pr = previews[url];
                  return (
                    <div key={i} className="border border-slate-200 rounded-xl p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[11px] text-slate-400">{section}</div>
                          <div className="text-sm text-slate-700 truncate">{url}</div>
                        </div>
                        <Button data-testid={`preview-btn-${i}`} onClick={() => previewUrl(url)} disabled={previewing === url}
                          className="bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg h-9 px-3 text-xs shrink-0">
                          {previewing === url ? <CircleNotch size={14} className="spin-slow" /> : "Preview"}
                        </Button>
                      </div>
                      {pr && (
                        <div data-testid={`preview-result-${i}`} className="mt-2 text-xs">
                          <div className={`flex items-center gap-1 font-medium ${pr.success ? "text-emerald-600" : "text-red-600"}`}>
                            {pr.success ? <CheckCircle size={13} weight="fill" /> : <XCircle size={13} weight="fill" />}
                            {pr.success ? pr.title : pr.error}
                          </div>
                          {pr.success && <p className="text-slate-500 mt-1 line-clamp-3 bg-slate-50 rounded p-2">{pr.text?.slice(0, 320)}…</p>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {selTemplate?.sections?.length > 0 && (
            <div className="card p-5" data-testid="section-assign-panel">
              <div className="flex items-center gap-2 text-slate-900 font-semibold"><Stack size={18} className="text-[#004182]" /> Per-section content &amp; display</div>
              <p className="text-xs text-slate-500 mt-1">For each section choose <b>Auto</b> (AI writes it), <b>Information</b> (show a file's data exactly), or <b>Graph</b> (build a chart from a dataset). Pick a data source file and/or paste content — <b>pasted content wins</b>.</p>
              <div className="mt-3 space-y-3 max-h-[600px] overflow-y-auto pr-1">
                {selTemplate.sections.map((s) => {
                  const ov = sectionOverrides[s.id] || { mode: "auto", docs: [], manual_text: "" };
                  const mode = ov.mode || "auto";
                  const hasManual = !!(ov.manual_text && ov.manual_text.replace(/<[^>]*>/g, "").trim());
                  const MODES = [{ v: "auto", n: "Auto (AI)" }, { v: "info", n: "Information" }, { v: "graph", n: "Graph" }];
                  return (
                    <div key={s.id} data-testid={`assign-section-${s.id}`} className="border border-slate-200 rounded-xl p-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-sm font-medium text-slate-800">{s.title}</div>
                        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid={`section-mode-${s.id}`}>
                          {MODES.map((m) => (
                            <button key={m.v} type="button" data-testid={`section-mode-${s.id}-${m.v}`}
                              onClick={() => setSectionMode(s.id, m.v)}
                              className={`text-[11px] px-2.5 py-1 transition-colors ${mode === m.v ? "bg-[#004182] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                              {m.n}
                            </button>
                          ))}
                        </div>
                      </div>
                      {mode !== "auto" && (
                        <div className="mt-2">
                          <div className="label-caps text-slate-400 mb-1">Data sources ({monthLabel(month)})</div>
                          {(ov.docs || []).map((dref, i) => (
                            <div key={i} data-testid={`section-doc-row-${s.id}-${i}`} className="flex items-start gap-2 mt-2">
                              <div className="flex-1 space-y-1.5">
                                <select data-testid={`section-doc-${s.id}-${i}`} value={dref.doc_id || ""}
                                  onChange={(e) => setSectionDocAt(s.id, i, e.target.value)}
                                  className="w-full h-9 rounded-lg border border-slate-200 bg-white text-slate-800 text-sm px-2 outline-none">
                                  <option value="">— Select a document —</option>
                                  {documents.map((d) => (
                                    <option key={d.id} value={d.id}>{d.name} ({d.doc_type})</option>
                                  ))}
                                </select>
                                <Input data-testid={`section-extract-${s.id}-${i}`} value={dref.extraction_prompt || ""}
                                  onChange={(e) => setSectionDocPrompt(s.id, i, e.target.value)}
                                  placeholder='What to extract from this file (optional) — e.g. "SATAT EOIs / LOIs table only"; empty = whole file as-is' />
                              </div>
                              <button type="button" data-testid={`section-doc-remove-${s.id}-${i}`} onClick={() => removeSectionDoc(s.id, i)}
                                className="mt-1.5 text-slate-300 hover:text-red-500 transition-colors" title="Remove document">
                                <XCircle size={20} />
                              </button>
                            </div>
                          ))}
                          <button type="button" data-testid={`section-doc-add-${s.id}`} onClick={() => addSectionDoc(s.id)}
                            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#004182] hover:text-[#002d5c] transition-colors">
                            <Plus size={14} weight="bold" /> Add document
                          </button>
                          {mode === "graph" && (
                            <p className="text-[11px] text-slate-400 mt-1.5">Each selected Excel becomes a chart under this section (a pasted table becomes the chart). PDF/Word can't be charted — its text is merged in instead.</p>
                          )}
                          {mode === "info" && (
                            <p className="text-[11px] text-slate-400 mt-1.5">All selected files are merged seamlessly into the section. Leave a file's instruction empty to include it exactly as-is.</p>
                          )}
                        </div>
                      )}
                      <div className="mt-2">
                        <div className="label-caps text-slate-400 mb-1 flex items-center gap-2">Or paste / type content {hasManual && <span className="text-emerald-700">· used verbatim (wins)</span>}</div>
                        <RichTextEditor testid={`assign-editor-${s.id}`} value={ov.manual_text || ""}
                          onChange={(html) => setSectionText(s.id, html)}
                          placeholder="Paste a table or type content — kept exactly as-is (in Graph mode, a pasted table becomes the chart)…" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6" data-testid="step-generate">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="card p-5"><div className="label-caps text-slate-500">Template</div><div className="text-slate-900 mt-1 font-medium">{selTemplate?.name}</div></div>
            <div className="card p-5"><div className="label-caps text-slate-500">Inputs</div><div className="text-slate-900 mt-1">
              {Object.values(sectionOverrides).filter((v) => v.mode === "graph").length} graph ·
              {" "}{Object.values(sectionOverrides).filter((v) => v.mode === "info").length} info ·
              {" "}{Object.values(sectionOverrides).filter((v) => v.manual_text && v.manual_text.replace(/<[^>]*>/g, "").trim()).length} verbatim ·
              {" "}{purpleCount} auto sources</div></div>
            <div className="card p-5"><div className="label-caps text-slate-500 mb-1.5">Issue No</div><Input data-testid="issue-no-input" value={issueNo} onChange={(e) => setIssueNo(e.target.value)} /></div>
            <div className="card p-5"><div className="label-caps text-slate-500 mb-1.5">Issue Date</div>
              <Input type="date" data-testid="issue-date-input" value={displayToInput(issueDate)}
                onChange={(e) => setIssueDate(inputToDisplay(e.target.value))} className="[color-scheme:light]" />
              <div className="text-[11px] text-slate-400 mt-1">Defaults to today · pick any date from the calendar</div>
            </div>
            <div className="card p-5"><div className="label-caps text-slate-500 mb-1.5">Report Month &amp; Year</div>
              <div className="flex gap-2">
                <select data-testid="report-month-select" value={month.split("-")[1]}
                  onChange={(e) => changeMonth(`${month.split("-")[0]}-${e.target.value}`)}
                  className="flex-1 h-10 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm px-3 outline-none">
                  {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}
                </select>
                <select data-testid="report-year-select" value={month.split("-")[0]}
                  onChange={(e) => changeMonth(`${e.target.value}-${month.split("-")[1]}`)}
                  className="w-28 h-10 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm px-3 outline-none">
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <label data-testid="carry-forward-toggle" className="card p-5 flex items-start gap-3 cursor-pointer">
              <Checkbox checked={carryForward} onCheckedChange={(v) => setCarryForward(!!v)} className="mt-0.5 data-[state=checked]:bg-[#004182] data-[state=checked]:border-[#004182]" />
              <div>
                <div className="text-slate-900 font-medium text-sm">Carry forward last month's figures</div>
                <div className="text-xs text-slate-500 mt-0.5">For sections with no new data this month, reuse the previous report's content instead of showing "Data Not Available".</div>
              </div>
            </label>
          </div>

          {/* REVIEW / PREVIEW */}
          <div className="card p-5" data-testid="review-panel">
            <div className="flex items-center gap-2 text-slate-900 font-semibold"><Eye size={18} className="text-[#004182]" /> Review before you proceed</div>
            <p className="text-xs text-slate-500 mt-1">A quick summary of how each section will be produced. Go Back to change anything.</p>
            <div className="mt-3 divide-y divide-slate-100 max-h-[340px] overflow-y-auto pr-1">
              {(selTemplate?.sections || []).map((s) => {
                const ov = sectionOverrides[s.id] || {};
                const mode = ov.mode || "auto";
                const docs = (ov.docs || []).filter((d) => d.doc_id);
                const hasManual = !!(ov.manual_text && ov.manual_text.replace(/<[^>]*>/g, "").trim());
                const MODE_LABEL = { auto: "Auto (AI)", info: "Information", graph: "Graph" };
                const modeCls = mode === "auto" ? "bg-slate-100 text-slate-600" : mode === "graph" ? "bg-violet-50 text-violet-700" : "bg-blue-50 text-blue-700";
                return (
                  <div key={s.id} data-testid={`review-section-${s.id}`} className="py-2.5 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800">{s.title}</div>
                      {(docs.length > 0 || hasManual) && (
                        <div className="text-xs text-slate-500 mt-0.5 space-y-0.5">
                          {docs.map((d, i) => (
                            <div key={i} className="truncate">• {docName(d.doc_id)}{d.extraction_prompt ? <span className="text-slate-400"> — “{d.extraction_prompt}”</span> : ""}</div>
                          ))}
                          {hasManual && <div className="text-emerald-700">• Pasted content (verbatim, wins)</div>}
                        </div>
                      )}
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${modeCls}`}>{hasManual ? "Verbatim" : MODE_LABEL[mode]}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card p-6 sm:p-8 flex flex-col items-center text-center">
            <div className="h-14 w-14 rounded-2xl bg-[#fef3e2] flex items-center justify-center"><Sparkle size={28} weight="duotone" className="text-[#F59E0B]" /></div>
            <div className="text-slate-900 mt-4 font-semibold text-lg">Save a draft or generate now</div>
            <p className="text-slate-500 text-sm mt-1 max-w-md">
              Save as a draft to finish later, or generate now — GPT-5.4 scrapes default sources, runs live web search, reads your documents, and drafts each section.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3 mt-6">
              <Button data-testid="save-draft-btn" onClick={saveDraft} disabled={savingDraft || generating}
                className="bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-2xl h-14 px-7 font-semibold gap-2 text-base">
                {savingDraft ? <><CircleNotch size={20} className="spin-slow" /> Saving…</> : <><FloppyDisk size={20} weight="bold" /> Save as Draft</>}
              </Button>
              <Button data-testid="run-generate-btn" onClick={generate} disabled={generating || savingDraft}
                className="gold-btn rounded-2xl px-8 font-semibold gap-2 h-14 text-base">
                {generating ? <><CircleNotch size={20} className="spin-slow" /> Starting…</> : <><Sparkle size={20} weight="bold" /> Generate Report</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="ghost" data-testid="wizard-back" disabled={step === 0}
          onClick={() => setStep((s) => s - 1)} className="text-slate-500 hover:text-slate-900">Back</Button>
        {step < 2 && (
          <Button data-testid="wizard-next" disabled={step === 0 && !selTemplate}
            onClick={() => setStep((s) => s + 1)} className="gold-btn rounded-xl px-6 font-semibold h-11">Continue</Button>
        )}
      </div>
    </div>
  );
}
